import type { Language } from '@/i18n/types';
import type { AppTranslations } from '@/i18n/app-translations.types';

// English is imported statically: it is the default language, the type source,
// and the instant fallback that guarantees translations are never undefined for
// the ~650 consumers that read `t.a.b.c` without null guards.
import { appTranslationsEn } from '@/i18n/app/en';

// French and Dutch load on demand via dynamic import() so they live in their own
// bundle chunks and are only downloaded for users whose language is set to them.
const loaders: Record<Exclude<Language, 'en'>, () => Promise<AppTranslations>> = {
  fr: () => import('@/i18n/app/fr').then((m) => m.default),
  nl: () => import('@/i18n/app/nl').then((m) => m.default),
};

// Module-level cache: each language is fetched at most once, then reused
// synchronously across every consumer for the rest of the session.
const cache: Partial<Record<Language, AppTranslations>> = {
  en: appTranslationsEn,
};


/** Returns a cached dictionary if loaded, else undefined (no fetch). */
export const getCachedLanguage = (
  language: Language
): AppTranslations | undefined => cache[language];

/**
 * Loads a language dictionary, returning a cached copy when available.
 * EN resolves synchronously; FR/NL resolve once their chunk has downloaded.
 */
export const preloadLanguage = async (
  language: Language
): Promise<AppTranslations> => {
  const cached = cache[language];
  if (cached) return cached;
  try {
    const loaded = await loaders[language as Exclude<Language, 'en'>]();
    if (!loaded || typeof loaded !== 'object') {
      return appTranslationsEn;
    }
    cache[language] = loaded;
    return loaded;
  } catch (err) {
    // Chunk failed to load — likely a stale SW cache serving an old hash.
    // Clear all SW caches and reload so the user gets the current build.
    if (
      typeof caches !== 'undefined' &&
      err instanceof Error &&
      (err.message.includes('Failed to fetch') || err.message.includes('404') || err.name === 'ChunkLoadError')
    ) {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
      window.location.reload();
    }
    return appTranslationsEn;
  }
};

export { appTranslationsEn };
